import { defineRouter } from '#q-app/wrappers'
import { createRouter, createMemoryHistory, createWebHistory, createWebHashHistory } from 'vue-router'
import { $rollbar } from '@/boot/rollbar'
import { applyRedirectMiddleware } from './redirect.middleware'
import { blockAppRoutesOnWeb } from './guards/appRouteGuard'
import { applyAdminRouteGuard } from './guards/adminRouteGuard'
import { setupReturnNavigationGuard } from './guards/returnNavigationGuard'
import routes from './routes'
import { toAndFromPathsAreEqual } from '@/utils/routes.util'
import { isChunkLoadingError } from '@/utils/error.util'
/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */
export default defineRouter(({ store }) => {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory

  const Router = createRouter({
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        // $logger.debug('SCROLL BEHAVIOR', savedPosition)
        return savedPosition
      } else if (to.query?.ns) {
        return null
      } else if (!toAndFromPathsAreEqual(to, from, { includeQuery: true })) {
        return { top: 0 }
      }

      return null
    },
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  })

  // Block /app/* routes on web in production (must run before redirect middleware)
  Router.beforeEach(blockAppRoutesOnWeb)

  // Apply redirect middleware before each route
  // ⚠️ IMPORTANT: There are also redirect rules in `useNavigationGuards.js`
  Router.beforeEach(applyRedirectMiddleware)

  // PEG-546: gate routes flagged with meta.requiresAdmin behind a
  // valid internal admin session (PEG-509). Retailer sessions are
  // silently redirected to the retailer dashboard home.
  Router.beforeEach(applyAdminRouteGuard)

  // Setup return navigation guard (detect back to choose items from item processing)
  setupReturnNavigationGuard(Router)

  // Handle router errors for dynamic imports
  Router.onError(error => {
    if (isChunkLoadingError(error)) {
      // Handle chunk loading errors - this often happens during deployments
      $rollbar.warning('Router chunk loading error', { error, url: window.location.href })

      // reload the page, but add a query param to prevent infinite reload loops
      const reloadUrl = new URL(window.location.href)

      // Only reload if the 'reload' query param is not already set
      if (!reloadUrl.searchParams.has('reload')) {
        reloadUrl.searchParams.set('reload', 'true')
        window.location.href = reloadUrl.toString()
      } else {
        // If we are already reloading, log the error and set the session error state
        // this will trigger a redirect to the error page
        $rollbar.error('Failed to reload chunks', { error, url: window.location.href })
        const sessionStore = useSessionStore(store)
        sessionStore.setErrorState(true) // Set session error state
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('Router error:', error)
    }
  })

  // expose router instance to window during Cypress tests
  if (window.Cypress) {
    window.$router = Router
  }

  return Router
})
